import React, { ChangeEvent, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import {
  DOMAIN_PREFIX,
  LANGUAGE_ENUMS,
  domainCode,
} from '../../../constants/staticPageConstants';
import { RECAPTCHA_SITE_KEY } from '../../Common/ContactSalesForm/constants';
import ClearLeadFormCall, { ILeadData } from '../../Common/LeadFormCall';
import { COUNTRY_CODES } from './constants';

interface DemoModalProps {
  domain?: number;
  modalRef: React.MutableRefObject<null>;
  demoPopup: (close: boolean) => void;
  language: LANGUAGE_ENUMS;
}

export const DemoModal = ({
  modalRef,
  demoPopup,
  language = LANGUAGE_ENUMS.English,
}: DemoModalProps): JSX.Element => {
  let domain = window.location.hostname;
  domain =
    domain.replace(/^www\./, '') + window.location.pathname.substring(0, 3);
  const CountryCode =
    domain === DOMAIN_PREFIX[domainCode.cleartaxBE]?.domain ? '+32' : '+966';
  domain === DOMAIN_PREFIX[domainCode.cleartaxMY]?.domain ? '+60' : '+966';
  const [inputValues, setInputValues] = useState({
    book_a_demo_name: '',
    book_a_demo_company_name: '',
    book_a_demo_countrycode: CountryCode,
    book_a_demo_phone: '',
    book_a_demo_company_email: '',
    book_a_demo_checkbox: false,
  });
  const [formSuccess, successfulForm] = useState(false);
  const [nameError, setNameError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [phoneError, setPhoneError] = useState(false);
  const [companyNameError, setCompanyNameError] = useState(false);
  const [checkboxError, setCheckboxError] = useState(false);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const setAllErrorFalse = () => {
    setNameError(false);
    setEmailError(false);
    setPhoneError(false);
    setCompanyNameError(false);
    setCheckboxError(false);
  };
  const handleOnChange = (
    event: ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { id, value } = event.target;
    setInputValues({ ...inputValues, [id]: value });
    setAllErrorFalse();
  };

  const handleOnCheckLegal = () => {
    setInputValues({
      ...inputValues,
      book_a_demo_checkbox: !inputValues.book_a_demo_checkbox,
    });
  };

  const validateEmail = (email: string) => {
    return String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      );
  };

  const validateInputs = () => {
    const {
      book_a_demo_name: name,
      book_a_demo_company_email: email,
      book_a_demo_phone: phone_number,
      book_a_demo_checkbox: checkbox,
      book_a_demo_company_name: company_name,
    } = inputValues;

    let isValid = true;
    if (!name) {
      setNameError(true);
      isValid = false;
    }
    const excludeDomains = [
      'gmail.com',
      'yahoo.com',
      'rediffmail.com',
      'hotmail.com',
    ];
    const excludedEmail = excludeDomains.some((domain) =>
      email.toLowerCase().endsWith(`@${domain}`)
    );
    if (!email || !validateEmail(email) || excludedEmail) {
      setEmailError(true);
      isValid = false;
    }
    if (!phone_number) {
      setPhoneError(true);
      isValid = false;
    }
    if (!company_name) {
      setCompanyNameError(true);
      isValid = false;
    }
    if (!checkbox) {
      setCheckboxError(true);
      isValid = false;
    }
    return isValid;
  };

  const submitButtonClicked = () => {
    const {
      book_a_demo_name: name,
      book_a_demo_company_email: email,
      book_a_demo_phone: phone_number,
      book_a_demo_countrycode: ISD,
      book_a_demo_company_name: company_name,
    } = inputValues;
    if (validateInputs()) {
      const recaptcha = recaptchaRef.current?.executeAsync();
      const urlParams = new URLSearchParams(window.location.search);
      const originCountry =
        domain === DOMAIN_PREFIX[domainCode.cleartaxMY]?.domain
          ? 'Malaysia'
          : 'Saudi Arabia';
      if (recaptcha) {
        recaptcha.then((token) => {
          ClearLeadFormCall.sendClearLeadData(
            {
              lead_data: {
                form_type: 'GCC-contact-sales-form',
                lead_type: 'Tax Experts',
                name: name,
                email: email,
                company_name: company_name,
                phone_number: phone_number,
                ISD: ISD,
                lead_source_url: window.location.href,
                utm_source: urlParams.get('utm_source'),
                utm_campaign: urlParams.get('utm_campaign'),
                utm_term: urlParams.get('utm_term'),
                utm_adgroup: urlParams.get('utm_adgroup'),
                utm_medium: urlParams.get('utm_medium'),
                utm_keyword: urlParams.get('utm_medium'),
              },
              lead_platforms: {
                salesforce: true,
                pardot: true,
              },
              extra_payload: {
                domain_name: domain,
                origin_country: originCountry,
              },
            } as ILeadData,
            token
          ).then((response) => {
            if (response.status == 201) {
              successfulForm(true);
            }
          });
        });
      }
    }
  };
  const direction = language === 'Arabic' ? 'rtl' : 'ltr';
  return (
    <div
      className="modal rounded-xl modal-open"
      style={{ direction: direction }}
      id="contact-sales"
      data-fold-location="navbar_button_Book a demo"
      data-testid="rad-modal"
      ref={modalRef}
    >
      <span
        className="cursor-pointer float-right mr-4 mt-6 hidden"
        onClick={() => demoPopup(false)}
        data-testid="close-demo-modal"
      >
        ✕
      </span>
      <div className="px-10 py-10">
        {!formSuccess ? (
          <div className="py-3">
            <div id="ksa-einvoicing-modal-wrapper">
              <div className="text-center">
                <span
                  id="CTAlabel"
                  className="text-s-18 text-font-500 font-bold"
                >
                  {language === 'Arabic'
                    ? 'احجز عرضًا تجريبيًا'
                    : 'Book a demo'}
                </span>

                <p className="text-s-14 pt-2">
                  {language === 'Arabic'
                    ? 'يرجى ملء التفاصيل الخاصة بك وسيتواصل ممثلنا معك قريبًا'
                    : 'Please fill in your details and our representative will get in touch with you shortly'}
                </p>
              </div>

              <p className="ksa-einvoicing-modal-error-msg text-red text-s-10 mb-4 text-center"></p>
              <form
                className="mt-4 contact-sales-form flex flex-wrap"
                data-identifier="ksa-einvoicing-modal"
                data-location=""
                data-category="home"
              >
                <div
                  className={
                    'w-2/5 sm:pr-0 sm:w-full sm:pl-0 ' +
                    (language === 'Arabic' ? 'pl-4' : 'pr-4')
                  }
                >
                  <div className="  pb-4">
                    <label htmlFor="book_a_demo_name" className="text-s-14">
                      {language === 'Arabic' ? 'اسم' : 'Name'}
                    </label>
                    <br />
                    <input
                      id="book_a_demo_name"
                      type="text"
                      className="border border-gray-300 rounded-sm mt-1 w-full"
                      style={{ height: '42px' }}
                      onChange={handleOnChange}
                    />
                    {nameError ? (
                      <span
                        id="ksa-einvoicing-username_error"
                        className="text-red-350 text-s-10 font-semibold"
                        style={{ display: 'inline-block' }}
                      >
                        Name cannot be empty
                      </span>
                    ) : null}
                  </div>
                  <div className="  pb-4">
                    <label
                      htmlFor="book_a_demo_company_name"
                      className="text-s-14"
                    >
                      {language === 'Arabic' ? 'اسم الشركة' : 'Company Name'}
                    </label>
                    <br />
                    <input
                      id="book_a_demo_company_name"
                      type="text"
                      className="border border-gray-300 rounded-sm mt-1 w-full"
                      style={{ height: '42px' }}
                      onChange={handleOnChange}
                    />
                    {companyNameError ? (
                      <span
                        id="ksa-einvoicing-username_error"
                        className="text-red-350 text-s-10 font-semibold"
                        style={{ display: 'inline-block' }}
                      >
                        Company name cannot be empty
                      </span>
                    ) : null}
                  </div>
                </div>
                <div className="w-3/5 sm:w-full">
                  <div className="inline-flex justify-between w-full">
                    <div className="w-4/12 pb-4">
                      <label
                        htmlFor="book_a_demo_countrycode"
                        className="text-s-14"
                      >
                        {language === 'Arabic'
                          ? 'الرمز الدولي'
                          : 'Country Code'}
                      </label>
                      <br />
                      <select
                        id="book_a_demo_countrycode"
                        className="w-full pl-0 text-s-14 rounded-md outline-none mt-1"
                        style={{ height: '42px', background: '#F5F5F5' }}
                        onChange={handleOnChange}
                      >
                        {domain ===
                        DOMAIN_PREFIX[domainCode.cleartaxBE]?.domain ? (
                          <>
                            <option value={COUNTRY_CODES.BE.code}>
                              {COUNTRY_CODES.BE.name} ({COUNTRY_CODES.BE.code})
                            </option>
                          </>
                        ) : (
                          <>
                            <option value={COUNTRY_CODES.SA.code}>
                              {COUNTRY_CODES.SA.name} ({COUNTRY_CODES.SA.code})
                            </option>
                            <option value={COUNTRY_CODES.BE.code}>
                              {COUNTRY_CODES.BE.name} ({COUNTRY_CODES.BE.code})
                            </option>
                          </>
                        )}
                        {domain ===
                        DOMAIN_PREFIX[domainCode.cleartaxMY]?.domain ? (
                          <>
                            <option value={COUNTRY_CODES.MY.code}>
                              {COUNTRY_CODES.MY.name} ({COUNTRY_CODES.MY.code})
                            </option>
                            <option value={COUNTRY_CODES.SA.code}>
                              {COUNTRY_CODES.SA.name} ({COUNTRY_CODES.SA.code})
                            </option>
                          </>
                        ) : (
                          <>
                            <option value={COUNTRY_CODES.SA.code}>
                              {COUNTRY_CODES.SA.name} ({COUNTRY_CODES.SA.code})
                            </option>
                            <option value={COUNTRY_CODES.MY.code}>
                              {COUNTRY_CODES.MY.name} ({COUNTRY_CODES.MY.code})
                            </option>
                          </>
                        )}
                        <option value={COUNTRY_CODES.AE.code}>
                          {COUNTRY_CODES.AE.name} ({COUNTRY_CODES.AE.code})
                        </option>
                        <option value={COUNTRY_CODES.OM.code}>
                          {COUNTRY_CODES.OM.name} ({COUNTRY_CODES.OM.code})
                        </option>
                        <option value={COUNTRY_CODES.BH.code}>
                          {COUNTRY_CODES.BH.name} ({COUNTRY_CODES.BH.code})
                        </option>
                        <option value={COUNTRY_CODES.KW.code}>
                          {COUNTRY_CODES.KW.name} ({COUNTRY_CODES.KW.code})
                        </option>
                        <option value={COUNTRY_CODES.QA.code}>
                          {COUNTRY_CODES.QA.name} ({COUNTRY_CODES.QA.code})
                        </option>
                        <option value={COUNTRY_CODES.IN.code}>
                          {COUNTRY_CODES.IN.name} ({COUNTRY_CODES.IN.code})
                        </option>
                      </select>
                    </div>
                    <div className="w-7/12 pb-4">
                      <label htmlFor="book_a_demo_phone" className="text-s-14">
                        {language === 'Arabic' ? 'رقم الهاتف' : 'Phone Number'}
                      </label>
                      <br />
                      <input
                        id="book_a_demo_phone"
                        type="text"
                        className="border border-gray-300 rounded-sm mt-1 w-full"
                        style={{ height: '42px' }}
                        onChange={handleOnChange}
                      />
                      {phoneError ? (
                        <span
                          id="ksa-einvoicing-username_error"
                          className="text-red-350 text-s-10 font-semibold"
                          style={{ display: 'inline-block' }}
                        >
                          Phone number format is wrong
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div className="pb-4">
                    <label
                      htmlFor="book_a_demo_company_email"
                      className="text-s-14"
                    >
                      {language === 'Arabic'
                        ? 'البريد الإلكتروني للشركة'
                        : 'Company Email'}
                    </label>
                    <br />
                    <input
                      id="book_a_demo_company_email"
                      type="text"
                      className="border border-gray-300 rounded-sm mt-1 w-full"
                      style={{ height: '42px' }}
                      onChange={handleOnChange}
                    />
                    {emailError ? (
                      <span
                        id="ksa-einvoicing-username_error"
                        className="text-red-350 text-s-10 font-semibold"
                        style={{ display: 'inline-block' }}
                      >
                        Email format is wrong
                      </span>
                    ) : null}
                  </div>
                </div>
                <div>
                  <input
                    type="checkbox"
                    data-testid="book_a_demo_checkbox"
                    onChange={handleOnCheckLegal}
                  />
                  <span className="text-s-12">
                    {' '}
                    {language === 'Arabic'
                      ? '	بإرسال النموذج ، فإنك توافق على السماح لمندوب ClearTax بالاتصال بك لتقديم عرض توضيحي لمنتجاتنا'
                      : 'By submitting the form, you agree to allow representatives of ClearTax to contact you for providing demonstration of our products.'}
                  </span>
                  {checkboxError ? (
                    <span
                      id="ksa-einvoicing-username_error"
                      className="text-red-350 text-s-10 font-semibold"
                      style={{ display: 'inline-block' }}
                    >
                      Please enable the checkbox
                    </span>
                  ) : null}
                </div>
                <div
                  className="w-full flex justify-center"
                  onClick={submitButtonClicked}
                >
                  <ReCAPTCHA
                    sitekey={RECAPTCHA_SITE_KEY.commonLead}
                    size="invisible"
                    ref={recaptchaRef}
                  />
                  <input
                    id="ksa-einvoicing-submit-form"
                    type="button"
                    value={
                      language === 'Arabic'
                        ? 'احجز عرضًا تجريبيًا'
                        : 'Book a demo'
                    }
                    className="button button-bluefilled cursor-pointer rounded-md mt-4 float-right w-1/3 sm:w-full"
                  />
                </div>
              </form>
            </div>
          </div>
        ) : (
          <div
            id="ksa-einvoicing-success-wrapper"
            className="text-center my-10"
          >
            <p className="text-base font-bold text-font-500">
              Thank you for your response
            </p>
            <p id="success-message" className="text-base text-font-500">
              Our representative will get in touch with you shortly.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
